import { useEffect, useRef } from "react";
import { useDict } from "@/i18n/useDict";
import { MODULE_GRID_ICONS, MODULE_GRID_AI_INDEX } from "@/i18n/icons";

const ModulesSection = () => {
  const { dict } = useDict();
  const gridRef = useRef<HTMLDivElement>(null);

  const modules = dict.modulesSection.items.map((m, i) => ({
    ...m,
    icon: MODULE_GRID_ICONS[i],
    ai: i === MODULE_GRID_AI_INDEX,
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    gridRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="module" className="py-20 md:py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">{dict.modulesSection.heading}</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {dict.modulesSection.subheading}
          </p>
        </div>
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
          {modules.map((m, i) =>
            m.ai ? (
              <div
                key={i}
                className="animate-on-scroll feature-card rounded-2xl p-6 cursor-default relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(99,102,241,0.06) 100%)",
                  border: "1.5px solid rgba(139,92,246,0.25)",
                  boxShadow: "0 4px 24px rgba(139,92,246,0.10)",
                }}
              >
                {/* subtle shimmer accent */}
                <div
                  className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full blur-2xl opacity-30"
                  style={{ background: "radial-gradient(circle, #a78bfa 0%, transparent 70%)" }}
                />
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)", color: "#fff" }}
                >
                  {m.icon}
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold" style={{ color: "#7c3aed" }}>{m.title}</h3>
                  <span
                    className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(139,92,246,0.15)", color: "#7c3aed" }}
                  >
                    {dict.modulesSection.aiBadge}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
              </div>
            ) : (
              <div
                key={i}
                className="animate-on-scroll feature-card rounded-2xl border border-border bg-card p-6 cursor-default"
                style={{ boxShadow: "var(--shadow-card)" }}
              >
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-primary mb-4">
                  {m.icon}
                </div>
                <h3 className="text-lg font-semibold text-foreground">{m.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default ModulesSection;
